import { useCallback, useState } from 'react'

import { TableFilters } from '@circlefin/components'
import { usePermission } from '@circlefin/permissions/react'
import { BURN_FEE_IMPROVEMENTS } from '@services/permissions'
import { feeDetailsSection } from '@services/sections/lib/billing'
import { TransWithLink } from '@shared/components/common'
import { ComplexTable, TableCard } from '@shared/components/tables'
import { Filters } from '@shared/components/tables/TableCard/Filters/Filters'
import { useFeeReportsLazyQuery } from '@shared/graphql'
import { default as useIsMounted } from 'ismounted'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { useFeeHistoryTableColumns } from './FeeHistoryTable.Columns'
import { CurrencyFilter } from './FeeHistoryTable.Filters'

import type {
  OnFetchDataProps,
  Row,
} from '@circlefin/components/lib/AdvancedTable'
import type { FilterDateRangeValue } from '@circlefin/components/lib/TableFilters/types'
import type { Currency, FeeReport } from '@shared/graphql'

const { Header, Title, Body } = TableCard

export interface TableFilterProps {
  /**
   * Fee report currency.
   */
  currency?: Currency
  /**
   * Date associated with this report.
   */
  valueDate?: FilterDateRangeValue
}

export interface FeeHistoryTableProps {
  /**
   * Controls the amount of lines in the table for each paginated page.
   * Defaults to 10.
   */
  pageSize?: number
  /**
   * Whether we want to enable pagination.
   * Defaults to true.
   */
  enablePagination?: boolean
}

export const FeeHistoryTable: React.FC<FeeHistoryTableProps> = ({
  pageSize = 10,
  enablePagination = true,
}) => {
  const { t } = useTranslation('settings')
  const columns = useFeeHistoryTableColumns()
  const [filters, setFilters] = useState<TableFilterProps>({})
  const router = useRouter()

  const [, { isAuthorized: burnFeeImprovementsEnabled }] = usePermission(
    BURN_FEE_IMPROVEMENTS,
  )

  const [fetchFeeHistory, { called, loading, data, refetch, error }] =
    useFeeReportsLazyQuery({
      variables: { page: { pageSize } },
      fetchPolicy: 'cache-and-network',
    })
  const isMounted = useIsMounted()

  const handleChange = useCallback((filters: TableFilterProps) => {
    setFilters(filters)
  }, [])

  const onRowClick = useCallback(
    ({ original }: Row<FeeReport>) => {
      if (!burnFeeImprovementsEnabled) return
      void router.push({
        pathname: feeDetailsSection.route,
        query: {
          id: original.id,
        },
      })
    },
    [router, burnFeeImprovementsEnabled],
  )

  const handleFetchData = useCallback(
    ({
      currentPageLink,
      filters: tableFilters,
    }: OnFetchDataProps<FeeReport, TableFilterProps>) => {
      if (isMounted.current) {
        const { from, to } = tableFilters.valueDate ?? {}

        void fetchFeeHistory({
          variables: {
            page: currentPageLink
              ? {
                  pageSize: currentPageLink.pageSize,
                  to: currentPageLink.to,
                  from: currentPageLink.from,
                  pageAfter: currentPageLink.pageAfter,
                  pageBefore: currentPageLink.pageBefore,
                }
              : {
                  pageSize,
                  to,
                  from,
                },
            filter: {
              currency: tableFilters.currency,
            },
          },
        })
      }
    },
    [fetchFeeHistory, isMounted, pageSize],
  )

  return (
    <TableCard>
      <Header>
        <div className="flex flex-col gap-2">
          <Title>{t`billing.feeHistory.title`}</Title>
          <span className="ml-5">
            <TransWithLink
              i18nKey="settings:billing.feeHistory.body1"
              urlI18nKey="settings:billing.feeHistory.learnMoreLink"
              variant="url"
            />
          </span>

          <span className="ml-5 type-body-sm">
            {t('billing.feeHistory.body2')}
          </span>
        </div>
      </Header>
      <Filters onChange={handleChange} size={9}>
        <TableFilters.DateRange
          name="valueDate"
          placeholder={t`common:dateRange`}
          size={3}
          start={5}
          isClearable
        />
        <CurrencyFilter name="currency" size={2} start={8} />
      </Filters>
      <Body>
        <ComplexTable<FeeReport, TableFilterProps>
          columns={columns}
          data={data?.feeReports}
          empty={{
            title: t`billing.feeHistory.table.empty.title`,
            subtitle: t`billing.feeHistory.table.empty.subtitle`,
          }}
          enableSorting={false}
          error={error}
          filters={filters}
          layout="auto"
          loading={!called || (!data && loading)}
          onFetchData={handleFetchData}
          onRowClick={onRowClick}
          pageSize={pageSize}
          pagination={enablePagination ? 'links' : 'default'}
          retry={refetch}
        />
      </Body>
    </TableCard>
  )
}
